import { lazy, type ReactNode, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { LoginPage, RequireAuth } from './features/auth';
import { APP_TARGET } from './lib/target';

// ---------------------------------------------------------------------------
// Embedded routes — flat top-level paths backed by the singleton
// workspace=embedded / project=local. apiFetch rewrites the cloud-shaped
// paths down to the board's flat /api surface. All embedded route bodies
// live in `routes/embedded/` and are lazy-loaded so navigation
// transitions only fetch what the page needs.
// ---------------------------------------------------------------------------

const EmbeddedLayout = lazy(() =>
  import('./routes/embedded/_layout').then((m) => ({ default: m.EmbeddedLayout })),
);
const EmbeddedDevicesRoute = lazy(() =>
  import('./routes/embedded/devices').then((m) => ({ default: m.EmbeddedDevicesRoute })),
);
const EmbeddedDeviceDetailRoute = lazy(() =>
  import('./routes/embedded/devices.$did').then((m) => ({
    default: m.EmbeddedDeviceDetailRoute,
  })),
);
const EmbeddedTagsRoute = lazy(() =>
  import('./routes/embedded/tags').then((m) => ({ default: m.EmbeddedTagsRoute })),
);
const EmbeddedSequencesRoute = lazy(() =>
  import('./routes/embedded/sequences').then((m) => ({ default: m.EmbeddedSequencesRoute })),
);
const EmbeddedDiagnosticsRoute = lazy(() =>
  import('./routes/embedded/diagnostics').then((m) => ({
    default: m.EmbeddedDiagnosticsRoute,
  })),
);
const EmbeddedSystemRoute = lazy(() =>
  import('./routes/embedded/system').then((m) => ({ default: m.EmbeddedSystemRoute })),
);

function suspense(node: ReactNode): ReactNode {
  return <Suspense fallback={<p className="p-6 text-sm">loading…</p>}>{node}</Suspense>;
}

const embeddedRoutes: RouteObject[] = [
  {
    path: '/',
    element: suspense(<EmbeddedLayout />),
    children: [
      { index: true, element: <Navigate to="/devices" replace /> },
      { path: 'login', element: <LoginPage /> },
      {
        path: 'devices',
        element: <RequireAuth>{suspense(<EmbeddedDevicesRoute />)}</RequireAuth>,
      },
      {
        path: 'devices/:did',
        element: <RequireAuth>{suspense(<EmbeddedDeviceDetailRoute />)}</RequireAuth>,
      },
      {
        path: 'tags',
        element: <RequireAuth>{suspense(<EmbeddedTagsRoute />)}</RequireAuth>,
      },
      {
        path: 'sequences',
        element: <RequireAuth>{suspense(<EmbeddedSequencesRoute />)}</RequireAuth>,
      },
      {
        path: 'diagnostics',
        element: <RequireAuth>{suspense(<EmbeddedDiagnosticsRoute />)}</RequireAuth>,
      },
      {
        path: 'system',
        element: <RequireAuth>{suspense(<EmbeddedSystemRoute />)}</RequireAuth>,
      },
      { path: '*', element: <Navigate to="/devices" replace /> },
    ],
  },
];

// ---------------------------------------------------------------------------
// buildRouter() returns a Promise<Router> so cloud routes can be
// dynamically imported. In embedded mode the cloud module is never
// imported, so Vite's bundler treats router.cloud.tsx as dead code and
// drops it (along with the firestore-heavy routes it transitively
// references).
// ---------------------------------------------------------------------------

export async function buildRouter() {
  if (APP_TARGET === 'embedded') {
    return createBrowserRouter(embeddedRoutes);
  }
  // Alias-swapped per build target: cloud/pi resolves to router.cloud.tsx
  // (full route map), embedded resolves to router.cloud.embedded.tsx
  // (empty stub never reached at runtime). Keeping the import behind an
  // alias key (rather than a literal `./router.cloud`) lets Vite's
  // resolve.alias drop the cloud route module + its transitive imports
  // (canvas, deploy, admin audit, etc.) from the embedded bundle.
  const { fullRoutes } = await import('@/router-cloud');
  return createBrowserRouter(fullRoutes);
}
